import type {
  AssessmentOverview,
  SubmissionErrorBody,
  SubmissionErrorCode,
  SubmissionResult,
  SubmitGateCondition,
} from '@assessments/shared'
import { apiFetch } from '../../services/apiClient.js'
import { assertOk } from '../../services/apiError.js'

export async function fetchAssessmentOverview(
  organizationSurveyId: string,
): Promise<AssessmentOverview> {
  const res = await apiFetch(`/assessments/${encodeURIComponent(organizationSurveyId)}/overview`)
  if (!res.ok) {
    throw new Error(`Failed to load overview (${res.status})`)
  }
  return (await res.json()) as AssessmentOverview
}

/** The server refused a submission for a stated reason (see contracts/submission-api.md). */
export class SubmissionRefusedError extends Error {
  readonly code: SubmissionErrorCode
  readonly unmet: SubmitGateCondition[]
  readonly submittedAt: string | null

  constructor(body: SubmissionErrorBody) {
    super(body.message)
    this.name = 'SubmissionRefusedError'
    this.code = body.code
    this.unmet = body.unmet ?? []
    this.submittedAt = body.submittedAt ?? null
  }
}

/**
 * Submits the assessment. Throws {@link SubmissionRefusedError} when the server names a
 * reason; an ApiError for anything else (including a 401 session expiry).
 */
export async function submitAssessment(organizationSurveyId: string): Promise<SubmissionResult> {
  const res = await apiFetch(
    `/assessments/${encodeURIComponent(organizationSurveyId)}/submission`,
    { method: 'POST', body: '{}' },
  )
  if (res.status === 403 || res.status === 409) {
    // Read from a clone so assertOk can still parse the body for the generic error.
    const body = (await res
      .clone()
      .json()
      .catch(() => undefined)) as Partial<SubmissionErrorBody> | undefined
    if (body?.code) throw new SubmissionRefusedError(body as SubmissionErrorBody)
  }
  await assertOk(res, 'submit assessment')
  return (await res.json()) as SubmissionResult
}
